import { fetchUserSettings, updateUserSetting } from '../api/services/ProfileService';
import { isPageInMenu } from './menu-tour-target';
import { findPageTourById } from './page-tours';
import { runTour, type TourHandle, type TourNavigation, type TourStep } from './tour-engine';

/**
 * Yonlendirme Faz 4.2 — Karsilama turu ("yolculuk"). Anasayfada bir giris
 * penceresiyle baslar, sonra onemli sayfalari sirayla acar ve her sayfada once
 * sayfanin ne ise yaradigini (baslik karti), sonra onemli parcalarini anlatir:
 * etkinlikler (her sey etkinlige katilmakla baslar) → firmalar → gorusme istekleri → oturumlar → masa/saat → randevular → mesajlar
 * → dijital kartvizit. Sonunda anasayfaya donup yardim ve profil menusunu
 * gosterir. Sayfa adimlari sayfa turlariyla ortaktir (`page-tours.ts`).
 *
 * Kullanicinin menusunde olmayan sayfa (modul kapali / rol) bastan atlanir;
 * sayfanin basligi cizilmezse (erisim yok, hata) o sayfa tur sirasinda atlanir.
 * "Tur goruldu" bilgisi kullanici ayarinda tutulur (`onboarding_tour_seen`),
 * boylece webde goren mobilde tekrar gormez.
 */

export const WELCOME_TOUR_SETTING_TYPE = 'onboarding_tour_seen';
export const WELCOME_TOUR_HOME_PATH = '/home';
/** Turu (yeniden) baslatmak icin yayinlanan olay; ana yerlesimdeki tur yoneticisi dinler. */
export const WELCOME_TOUR_START_EVENT = 'wc:welcome-tour:start';

/** Yolculukta sirayla gezilen sayfalar (`PAGE_TOURS` kimlikleri). */
export const JOURNEY_PAGE_IDS = [
    'events',
    'companyList',
    'meetingRequests',
    'sessions',
    'tables',
    'myAppointments',
    'messages',
    'digitalCards',
] as const;

/** Sayfa basligi bu sure icinde cizilmezse sayfa atlanir (veri gec yuklenebilir). */
export const JOURNEY_PAGE_WAIT_MS = 6000;
/** Sayfanin diger parcalari icin bekleme (baslik okunurken veri genelde yuklenmis olur). */
export const JOURNEY_DETAIL_WAIT_MS = 1500;

const homeStep = (id: 'intro' | 'help' | 'profile', target?: Pick<TourStep, 'targets' | 'side' | 'align'>): TourStep => ({
    titleKey: `tour.journey.${id}.title`,
    descKey: `tour.journey.${id}.desc`,
    path: WELCOME_TOUR_HOME_PATH,
    ...(target ? { ...target, waitMs: JOURNEY_DETAIL_WAIT_MS } : {}),
});

/**
 * Turun adimlari: giris, kullaniciya acik her sayfanin adimlari, yardim ve
 * profil menusu. Bir sayfanin adimlari ayni bolumdedir (`sectionId` = sayfa kimligi).
 */
export const buildJourneyPlan = (isPageAvailable: (path: string) => boolean = isPageInMenu): TourStep[] => {
    const pageSteps = JOURNEY_PAGE_IDS.flatMap((pageId) => {
        const page = findPageTourById(pageId);
        if (!page || !isPageAvailable(page.path)) return [];
        return page.steps.map(
            (step, index): TourStep => ({
                ...step,
                path: `/${page.path}`,
                labelKey: page.labelKey,
                sectionId: page.pageId,
                waitMs: index === 0 ? JOURNEY_PAGE_WAIT_MS : JOURNEY_DETAIL_WAIT_MS,
            }),
        );
    });

    return [
        homeStep('intro'),
        ...pageSteps,
        homeStep('help', { targets: ['page-tips', 'support'], side: 'top', align: 'end' }),
        homeStep('profile', { targets: ['profile-menu'], side: 'bottom', align: 'end' }),
    ];
};

/**
 * Turda bastan sona gorulen sayfalar: sayfanin basligi gosterildi ve kullanici
 * sayfanin son adimina (ya da daha ilerisine) ulasti. Bu sayfalarin sayfa
 * turlari tur bitince "goruldu" sayilir.
 */
export const pagesCoveredByJourney = (plan: TourStep[], shownIndexes: ReadonlySet<number>): string[] => {
    const furthest = Math.max(-1, ...shownIndexes);
    return JOURNEY_PAGE_IDS.filter((pageId) => {
        const start = plan.findIndex((step) => step.sectionId === pageId);
        if (start < 0 || !shownIndexes.has(start)) return false;
        let end = start;
        while (plan[end + 1]?.sectionId === pageId) end += 1;
        return furthest >= end;
    });
};

// ---------- "Tur goruldu" bilgisi ----------

export const tourSeenStorageKey = (userId: string) => `wc.tour.seen.${userId}`;

export const readLocalTourSeen = (userId: string) => {
    try {
        return localStorage.getItem(tourSeenStorageKey(userId)) === '1';
    } catch {
        return false;
    }
};

const storeLocalTourSeen = (userId: string) => {
    try {
        localStorage.setItem(tourSeenStorageKey(userId), '1');
    } catch {
        // Depolama kapaliysa sunucudaki bilgi yeterli.
    }
};

type UserSettingRow = { type?: string; status?: boolean };

export const isTourSeenInSettings = (rows: unknown) =>
    Array.isArray(rows) &&
    rows.some((row: UserSettingRow) => row?.type === WELCOME_TOUR_SETTING_TYPE && row?.status === true);

/**
 * Tur daha once goruldu mu? Once cihazdaki bayrak, yoksa kullanici ayari okunur.
 * Ayar okunamazsa tur bu kez gosterilmez (her hatada tekrar acilip rahatsiz
 * etmesin); cihaz bayragi yazilmadigi icin sonraki ziyarette yeniden sorulur.
 */
export const resolveTourSeen = async (userId: string): Promise<boolean> => {
    if (readLocalTourSeen(userId)) return true;
    try {
        const response: any = await fetchUserSettings();
        const seen = isTourSeenInSettings(response?.data?.data ?? response?.data);
        if (seen) storeLocalTourSeen(userId);
        return seen;
    } catch {
        return true;
    }
};

/** Tur bitince ya da atlaninca: cihaza hemen, sunucuya arka planda yazilir. */
export const markTourSeen = (userId: string) => {
    if (!userId) return;
    storeLocalTourSeen(userId);
    Promise.resolve()
        .then(() => updateUserSetting({ type: WELCOME_TOUR_SETTING_TYPE, status: true }))
        .catch(() => {
            // Sunucuya yazilamazsa cihaz bayragi tekrar gosterilmesini engeller.
        });
};

// ---------- Tur ----------

let welcomeTourActive = false;

/** Karsilama turu su an acik mi? (Sayfa turlari ust uste binmesin diye.) */
export const isWelcomeTourActive = () => welcomeTourActive;

/** Turu herhangi bir sayfadan (yeniden) baslatma istegi. */
export const requestWelcomeTour = () => window.dispatchEvent(new Event(WELCOME_TOUR_START_EVENT));

export type WelcomeTourCloseSummary = { coveredPageIds: string[] };

/**
 * Yolculuk turunu baslatir. `onClose`, tur bitirildiginde, atlandiginda ya da
 * kapatildiginda bir kez cagrilir; bastan sona gorulen sayfalari bildirir.
 */
export const startWelcomeTour = ({
    t,
    navigation,
    onClose,
}: {
    t: (key: string) => string;
    navigation: TourNavigation;
    onClose: (summary: WelcomeTourCloseSummary) => void;
}): TourHandle => {
    const plan = buildJourneyPlan();
    welcomeTourActive = true;
    return runTour({
        steps: plan,
        t,
        navigation,
        onClose: ({ shownIndexes }) => {
            welcomeTourActive = false;
            onClose({ coveredPageIds: pagesCoveredByJourney(plan, shownIndexes) });
        },
    });
};
